import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { runtimeConfig } from "src/services/runtimeConfig";

// DNC-463: the C# invoice view — the React port of b-invoice.adp, and a drop-in for it. It takes the
// SAME URL parameters as the .adp so the two can be compared side by side and the links swapped later:
//   /invoice?id=114540                    → screen view (with Prev/Next/Print chrome)
//   /invoice?id=114540&emailing_invoice=1 → clean "emailing" view (chrome-free), the email/PDF render
//   &debug=1        → sysadmin: red purchase-id links to the classic row editor + the hidden zero-quantity lines
//   &fromSysadmin=1 → no Account Summary / Statement / Change Plan links (sysadmin billing pages open it so)
//   &showprint=1    → the printed look on screen: black-and-white, no toolbar
//   &personId=N     → sysadmin: the contact to show when the account has none yet (Signup Wizard)
// debug and fromSysadmin carry over to Prev/Next, as the .adp's navigation form does.
// The invoice body is rendered on the server (one shared renderer) and returned as a complete,
// self-contained HTML document; this page shows it in a same-origin srcDoc iframe (full style isolation,
// yet Print/auto-size still work) and overlays the interactive chrome. The route uses PublicLayout, so
// like the .adp it is a standalone page, not wrapped in the app's nav.

interface InvoiceViewResponse {
  invoiceNum: string;
  accountNum: string;
  bodyHtml: string; // a complete <html> document
  prevInvoiceId?: number | null;
  nextInvoiceId?: number | null;
  isSysadmin: boolean;
  // b-invoice.adp's plan link: "Change Plan", "Reinstate" (red) or none.
  changePlanLink?: "Change Plan" | "Reinstate" | null;
  // Sysadmins only: what the .adp's "Sysadmin Email" button pre-fills on the classic sendmail.adp.
  sysadminEmail?: { to: string; cc: string; subject: string } | null;
}

// An id the API can bind: digits only, and within a C# int (a larger number would 400 the whole request).
function isIntId(value: string | null): value is string {
  return !!value && /^\d+$/.test(value) && Number(value) <= 2147483647;
}

// Port of the .adp's am_boolvar: false only for "", 0, f, false, off, no, n (any case); anything else is true.
function isTrue(value: string | null): boolean {
  if (!value) return false;
  return !["0", "f", "false", "off", "no", "n"].includes(value.toLowerCase());
}

function Invoice() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { csrfToken } = useAppSelector(selectAuth);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const id = searchParams.get("id");
  // emailing_invoice=1 → the clean, chrome-free view (the .adp's emailing variant).
  const chromeless = isTrue(searchParams.get("emailing_invoice"));
  // debug is never in the emailing view, and showprint applies to the screen view only (the server enforces both too).
  // Whether debug is allowed (sysadmin only) is the server's call, as on the .adp - the page follows its isSysadmin.
  const debugRequested = isTrue(searchParams.get("debug")) && !chromeless;
  const fromSysadmin = isTrue(searchParams.get("fromSysadmin"));
  const showprint = isTrue(searchParams.get("showprint")) && !chromeless;
  // Only a numeric personId is passed on (the API binds it as a number; anything else would be a 400).
  const personIdParam = searchParams.get("personId");
  const personId = isIntId(personIdParam) ? personIdParam : null;

  const [data, setData] = useState<InvoiceViewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The server rendered the debug links only for a sysadmin.
  const debug = debugRequested && data?.isSysadmin === true;

  useEffect(() => {
    // The .adp's messages. Only a plain number reaches the API: anything else (e.g. "114540?debug=1", a mistyped "&")
    // would otherwise slip extra parameters into the request path.
    if (!id) {
      setError("Page missing required parameter id");
      setIsLoading(false);
      return;
    }
    if (!isIntId(id)) {
      setError(`${id} is not a valid invoice id`);
      setIsLoading(false);
      return;
    }
    // Prev/Next change the id while a request may still be in flight: drop any response for an id the page
    // has already moved away from, so a slow earlier response can't overwrite the invoice now shown.
    let isCurrent = true;
    void fetchInvoice(id, () => isCurrent);
    return () => {
      isCurrent = false;
    };
  }, [id, chromeless, debugRequested, showprint, personId]);

  async function fetchInvoice(invoiceId: string, isCurrent: () => boolean) {
    setIsLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (chromeless) query.set("emailing_invoice", "1"); // the emailing document: no print stylesheet, as on the .adp
      if (debugRequested) query.set("debug", "1");
      if (showprint) query.set("showprint", "1");
      if (personId) query.set("personId", personId);
      const qs = query.toString();
      const response = await callApi(`invoice/${invoiceId}${qs ? `?${qs}` : ""}`, HttpMethod.GET);
      if (!isCurrent()) return;
      if (response?.type === "FAILED") {
        setError(response.message || "Failed to load invoice.");
        return;
      }
      setData(response);
    } catch (e: any) {
      if (isCurrent()) setError(e?.message || "Failed to load invoice.");
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }

  // Keep the emailing_invoice mode, and (like the .adp's navigation form) fromSysadmin and debug.
  function goToInvoice(targetId: number) {
    const query = new URLSearchParams({ id: String(targetId) });
    if (chromeless) query.set("emailing_invoice", "1");
    if (fromSysadmin) query.set("fromSysadmin", "1");
    if (debug) query.set("debug", "1");
    navigate(`/invoice?${query}`);
  }

  // srcDoc is same-origin, so we can read the rendered height and grow the iframe to fit (no inner scroll).
  function sizeIframeToContent() {
    const iframe = iframeRef.current;
    const doc = iframe?.contentWindow?.document;
    if (!iframe || !doc?.body) return;
    iframe.style.height = `${doc.body.scrollHeight + 24}px`;
  }

  function printInvoice() {
    iframeRef.current?.contentWindow?.print();
  }

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border" aria-hidden="true" />
        <span className="visually-hidden" role="status">Loading invoice…</span>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger m-4">{error}</div>;
  }

  if (!data) return null;

  const adminBase = `${runtimeConfig.classicUrl}/app/admin`;
  const debugQs = debug ? "?debug=1" : "";
  // The emailing render (the .adp's emailing_invoice=1), server-side, for sendmail.adp to fetch.
  const emailFetchUrl =
    `${runtimeConfig.apiUrl}/invoice/${encodeURIComponent(id ?? "")}/document?format=html` +
    (personId ? `&personId=${encodeURIComponent(personId)}` : "");

  // sandbox: no allow-scripts, so nothing in the invoice HTML can ever run script, even if a description slipped
  // past the server-side escaping. allow-same-origin lets this page read the frame's height; allow-modals lets
  // Print call the frame's print(). (Never add allow-scripts alongside allow-same-origin: the frame could then
  // remove its own sandbox.) allow-popups(-to-escape-sandbox) only so the debug links - full classic-site addresses with
  // target=_blank, written by the server - can open the row editor in a normal tab.
  const invoiceFrame = (
    <iframe
      ref={iframeRef}
      title={`Invoice ${data.invoiceNum}`}
      sandbox={debug ? "allow-same-origin allow-modals allow-popups allow-popups-to-escape-sandbox" : "allow-same-origin allow-modals"}
      srcDoc={data.bodyHtml}
      onLoad={sizeIframeToContent}
      style={{ width: "100%", border: "none", overflow: "hidden", display: "block" }}
    />
  );

  // Emailing view (emailing_invoice=1): bare white page, no app padding/background — just the invoice
  // document, matching the .adp's emailing output.
  // showprint=1: the print stylesheet hides all of the .adp's chrome (its buttons and the printhide links).
  if (chromeless || showprint) {
    return <div style={{ background: "#FFFFFF", minHeight: "100vh" }}>{invoiceFrame}</div>;
  }

  return (
    <div className="container-fluid py-3">
      {/* Toolbar and invoice share one width so the right-aligned actions line up with the invoice's edge. */}
      <div style={{ maxWidth: 740 }}>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={!data.prevInvoiceId}
            onClick={() => data.prevInvoiceId && goToInvoice(data.prevInvoiceId)}
          >
            <i className="bi bi-arrow-left" /> Prev
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={!data.nextInvoiceId}
            onClick={() => data.nextInvoiceId && goToInvoice(data.nextInvoiceId)}
          >
            Next <i className="bi bi-arrow-right" />
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={printInvoice}>
            <i className="bi bi-printer" /> Print
          </button>
          {!fromSysadmin && (
            <>
              <a className="btn btn-outline-secondary btn-sm" href={`${adminBase}/b-main.adp${debugQs}`} target="_blank" rel="noopener noreferrer">
                Account Summary
              </a>
              <a className="btn btn-outline-secondary btn-sm" href={`${adminBase}/b-statement.adp${debugQs}`} target="_blank" rel="noopener noreferrer">
                Statement
              </a>
            </>
          )}
          {!fromSysadmin && data.changePlanLink && (
            // Both open the classic change-plan wizard (ims_dnc_billing_changePlanUrl b-change1.html).
            <a
              className={`btn btn-sm ${data.changePlanLink === "Reinstate" ? "btn-outline-danger" : "btn-outline-secondary"}`}
              href={`${runtimeConfig.classicUrl}/NS/am/Wizard2/changeplan/b-change1.html?resetWizard=1`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.changePlanLink}
            </a>
          )}
          {data.sysadminEmail && (
            // The .adp's "Sysadmin Email": opens the classic sendmail.adp pre-filled. sendmail.adp fetches the body from
            // `fetch` server-side, forwarding this browser's cookies (reuseAuth), so it gets our server-rendered emailing
            // document - the same HTML the .adp's emailing_invoice=1 gave it.
            <form method="post" action={`${runtimeConfig.classicUrl}/app/sysadmin/sendmail.adp`} target="_blank" className="ms-auto m-0">
              <input type="hidden" name="CSRFToken" value={csrfToken ?? ""} />
              <input type="hidden" name="debug" value="1" />
              <input type="hidden" name="fetch" value={emailFetchUrl} />
              <input type="hidden" name="reuseAuth" value="1" />
              <input type="hidden" name="to" value={data.sysadminEmail.to} />
              <input type="hidden" name="cc" value={data.sysadminEmail.cc} />
              <input type="hidden" name="subject" value={data.sysadminEmail.subject} />
              <input type="hidden" name="HTMLFileName" value="invoice.html" />
              <input type="hidden" name="bodyType" value="1" />
              <input type="hidden" name="from" value="billing@DNC.com (DNC.com)" />
              <button type="submit" className="btn btn-outline-secondary btn-sm">
                <i className="bi bi-envelope" /> Sysadmin Email
              </button>
            </form>
          )}
          {data.isSysadmin && (
            <button type="button" className={`btn btn-outline-secondary btn-sm${data.sysadminEmail ? "" : " ms-auto"}`} onClick={() => window.close()}>
              Close window
            </button>
          )}
        </div>

        {invoiceFrame}
      </div>
    </div>
  );
}

export default Invoice;
